import { useWriteQueue } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * SaveStatusIndicator silently clears completed saves and
 * shows error state with retry/dismiss options if saves fail.
 */
const SaveStatusIndicator = observer(() => {
  const writeQueue = useWriteQueue();
  const { status, hasPendingItems, hasFailedItems } = writeQueue;

  // Silently clear completed items when all pending saves finish
  useEffect(() => {
    if (status.saved > 0 && !hasPendingItems) {
      writeQueue.clearCompleted();
    }
  }, [status.saved, hasPendingItems, writeQueue]);

  const handleRetry = () => {
    writeQueue.retryFailed();
  };

  const handleDismiss = () => {
    writeQueue.discardFailed();
  };

  // Show error state with retry option
  if (hasFailedItems) {
    return (
      <Container data-testid="save-status-indicator-error">
        <StatusContent>
          <ErrorIcon>!</ErrorIcon>
          <StatusText>
            {status.failed} clip{status.failed !== 1 ? 's' : ''} failed to save
          </StatusText>
        </StatusContent>
        <ActionsRow>
          <ActionButton onClick={handleRetry}>Retry</ActionButton>
          <ActionButton $secondary onClick={handleDismiss}>
            Dismiss
          </ActionButton>
        </ActionsRow>
      </Container>
    );
  }

  return null;
});

export default SaveStatusIndicator;

// --- Styled Components ---

const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Container = styled.div`
  position: fixed;
  bottom: 80px;
  right: 16px;
  z-index: 9999;
  background-color: color-mix(in srgb, var(--destructive) 15%, transparent);
  border: 1px solid color-mix(in srgb, var(--destructive) 40%, transparent);
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.3),
    0 2px 4px -1px rgba(0, 0, 0, 0.2);
  animation: ${slideIn} 0.2s ease-out;
  min-width: 200px;
`;

const StatusContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusText = styled.span`
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  flex: 1;
`;

const ErrorIcon = styled.div`
  width: 18px;
  height: 18px;
  background-color: var(--destructive);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const ActionButton = styled.button<{ $secondary?: boolean }>`
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: background-color 0.15s ease;

  ${props =>
    props.$secondary
      ? `
        background-color: transparent;
        color: var(--text-muted);
        &:hover {
          background-color: var(--surface);
        }
      `
      : `
        background-color: var(--destructive);
        color: white;
        &:hover {
          background-color: var(--destructive);
        }
      `}
`;
